import { NotifyHelper } from 'classes/helpers/notify.helper';
import { t } from 'i18next';
import { RestartMode } from 'lib_ts/enums/admin.enums';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { MachinesRoutes } from 'lib_ts/enums/routing/routes/machines';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineHardwareConfigBackup } from 'lib_ts/interfaces/i-machine-hw-config-backup';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { BaseRESTService } from 'services/_base-rest.service';

export class MachinesService extends BaseRESTService {
  private static instance: MachinesService;
  static getInstance(): MachinesService {
    if (!MachinesService.instance) {
      MachinesService.instance = new MachinesService();
    }

    return MachinesService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Machines,
    });
  }

  /** for users, returns machines in user's team */
  async getTeamMachines(): Promise<IMachine[]> {
    return await this.get({
      uri: MachinesRoutes.GetTeamMachines,
    });
  }

  /** machineID like 'Sandbox' */
  async getByMachineID(machineID: string): Promise<{
    machine: IMachine;
    connected: boolean;
    calibrated: boolean;
  }> {
    return await this.get({
      uri: `${MachinesRoutes.GetByMachineID}/${machineID}`,
    });
  }

  async getMachineHWConfigs(
    ids: string[]
  ): Promise<IMachineHardwareConfigBackup[]> {
    return await this.post(
      {
        uri: MachinesRoutes.GetHwConfigs,
      },
      ids
    );
  }

  // list of all models used by this machine (e.g. for any ball type)
  async getMachineModels(machineID: string): Promise<IMachineModel[]> {
    return await this.get({
      uri: `${MachinesRoutes.GetMachineModels}/${machineID}`,
    });
  }

  async getMachineModel(
    machineID: string,
    modelID: string
  ): Promise<IMachineModel> {
    return await this.get({
      uri: `${MachinesRoutes.GetMachineModel}/${machineID}/${modelID}`,
    });
  }

  /*
      process => restart the Arc processes on the machine
      os => restart OS on the machine
    */
  async restart(mode: RestartMode, machineIDs: string[]): Promise<void> {
    try {
      switch (mode) {
        case RestartMode.arc: {
          NotifyHelper.info({
            message_md: `Restart FW requested...`,
          });
          break;
        }

        case RestartMode.os: {
          NotifyHelper.info({
            message_md: `Restart OS requested...`,
          });
          break;
        }

        default: {
          console.warn(`Encountered unexpected restart mode: ${mode}`);
          return;
        }
      }

      return await this.post(
        { uri: `${MachinesRoutes.RestartMachine}/${mode}` },
        machineIDs
      );
    } catch (e) {
      console.error(e);
    }
  }

  async update(
    id: string,
    data: Partial<IMachine>
  ): Promise<IMachine | undefined> {
    if (!id) {
      NotifyHelper.warning({ message_md: 'Cannot update machine without ID' });
      return;
    }

    delete data._id;

    return this.put(
      {
        uri: MachinesRoutes.UpdateMachine,
        params: {
          machine_id: id,
        } as any,
      },
      data
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as IMachine;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return undefined;
      });
  }
}
